import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import type { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Typography, formatDate } from 'enable-ui';

import {
  FunctionalAbilityTableBody,
  FunctionalAbilityTableHeader,
} from 'modules/learners/styles/common';

import type { SurveyResult, SurveyResultReport } from '../core/type';

import { cloneDeep } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { getSurveyResult } from '../core/services';

interface Props {
  visible: boolean;
  learnerId: string;
  showDetail: boolean;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const SurveyResultDrawer = ({ visible, learnerId, showDetail }: Props) => {
  const [surveyResult, setSurveyResult] = useState<SurveyResultReport | null>(null);
  const survey = surveyResult?.survey;
  const results = surveyResult?.results || [];
  const chartResults = cloneDeep(results).reverse();

  const [detail, setDetail] = useState<SurveyResult | null>(null);

  const options: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 5,
        ticks: {
          stepSize: 1,
          color: '#808080',
        },
      },
      x: {
        ticks: {
          maxTicksLimit: 8,
          color: '#808080',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const labels = chartResults.map((el) => formatDate(el.createdAt, 'dd MMM'));

  const data: ChartData<'line', number[], string> = {
    labels,
    datasets: [
      {
        fill: true,
        label: 'Score',
        data: chartResults.map((el) => el.surveyOption.index),
        borderColor: '#383ECD',
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);

          gradient.addColorStop(0, 'rgba(182, 190, 242, 1)');
          gradient.addColorStop(1, 'rgba(182, 190, 242, 0.00)');

          return gradient;
        },
      },
    ],
  };

  useEffect(() => {
    if (visible) {
      getSurveyResult(learnerId, (rs) => {
        setSurveyResult(rs);

        if (rs?.results?.length && showDetail) {
          setDetail(rs.results[0]);
        }
      });
    } else {
      setDetail(null);
    }
  }, [visible]);

  return (
    <div
      style={{
        overflow: 'hidden',
        display: 'flex',
        width: detail ? 960 : 480,
        transition: 'all 0.5s',
      }}
    >
      <div style={{ padding: 32, maxHeight: '100vh', overflow: 'auto', minWidth: 480 }}>
        <Typography variant="h3" color={COLOR.surfaceNeutralBold['neutral-11']} weight={500}>
          Functional Mobility Scale history
        </Typography>
        <div className="border-container p-16" style={{ margin: '40px 0' }}>
          <Line options={options} data={data} />
        </div>
        <div className="border-container p-16">
          <FunctionalAbilityTableHeader>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Score
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Completed on
            </Typography>
          </FunctionalAbilityTableHeader>
          {results.map((el, index) => (
            <FunctionalAbilityTableBody
              $bgColor={`${
                el.id === detail?.id
                  ? 'rgba(0, 0, 0, 0.05)'
                  : (index + 1) % 2 === 0
                    ? COLOR_CONFIG.doctor
                    : COLOR_CONFIG.white
              }`}
              className="bg-hover"
              onClick={() => setDetail(el)}
            >
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                }}
              >
                {el.surveyOption.index}
              </Typography>
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                }}
              >
                {formatDate(el.createdAt, 'dd MMM yyyy')}
              </Typography>
            </FunctionalAbilityTableBody>
          ))}
        </div>
      </div>
      {detail ? (
        <div
          style={{
            padding: 32,
            maxHeight: '100vh',
            overflow: 'auto',
            minWidth: 480,
            minHeight: '100vh',
            borderLeft: '1px solid rgba(0, 0, 0, 0.25)',
          }}
        >
          <Typography
            style={{
              color: '#222',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Functional Mobility Scale result
          </Typography>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 6,
              marginBottom: 24,
            }}
          >
            <Typography
              style={{
                color: '#222',
                fontSize: 26,
                fontWeight: 500,
              }}
            >
              {detail.surveyOption.index}
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 400,
              }}
            >
              {formatDate(detail.createdAt, 'dd MMM yyy')}
            </Typography>
          </div>
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 12,
              border: '1px solid rgba(0, 0, 0, 0.25)',
            }}
          >
            <Typography variant="body-2">{survey?.title}</Typography>
            <Typography
              variant="body-3"
              style={{
                color: COLOR_CONFIG['neutral-text'],
                fontWeight: 600,
                marginTop: 24,
              }}
            >
              Answer
            </Typography>
            <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
              {detail.surveyOption.index} - {detail.surveyOption.value}
            </Typography>
          </div>
        </div>
      ) : null}
    </div>
  );
};
